import { createApp, Plugin } from 'vue'
import axios from 'axios'
import '@/plugins/dragPolyfill'
import * as Sentry from '@sentry/vue';
import { getFingerprint } from '@thumbmarkjs/thumbmarkjs';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { plugin as InputFacade } from 'vue-input-facade';

import { RootActions } from '@/store/types/action-types';
import { RootMutations } from '@/store/types/mutation-types';
import { registerWorker } from '@/utils/registerWorker';
import { recoverFromStaleBuild } from '@/bundles/App/helpers/staleBuildRecovery';
import { registerNormalizeHtmlDirective } from '@/plugins/normalizeHtmlDirective';
import { eventBus } from '@/eventbus/eventbus';
import { initSessionRecorder } from '@/plugins/sessionRecorder';
// BETA-CLEANUP: drop with the appChannel / app_channel lines below
import { isBetaApp } from '@/bundles/App/helpers/betaChannel';
import { initGtm } from '@/bundles/Analytics/helpers/gtm';
import { trackApiRequests } from '@/bundles/Analytics/helpers/apiRequests';
import { trackWebVitals } from '@/bundles/Analytics/helpers/webVitals';

import i18n from './bundles/Localization';
import * as filters from './utils/filters'
import vuetify from './plugins/vuetify'
// The app's sass must load AFTER Vuetify's, exactly as `vuetify/dist/vuetify.min.css`
// did under Vuetify 2 — otherwise every app override at equal specificity
// (`.pipeline-filters-bar { display: flex }` against Vuetify's `.v-card { display:
// block }`) loses on source order. Vuetify 3 ships its component CSS as imports
// inside each component's JS, which the plugin above pulls in, so these imports
// sit after it. vite-plugin-vuetify compiles 'vuetify/styles' and every
// component stylesheet from sass with sass/vuetify-settings.scss forwarded
// first, so the app's values for Vuetify's own SASS variables apply everywhere.
import 'vuetify/styles';
import '../sass/vuetify3-parity.scss';
import '../sass/stylesheets.sass';
import '../sass/styles.scss';
import { placeholderCompanyImg, placeholderListingImg, placeholderPropertyImg } from './settings'
import store from './store/store'
import router from './router/router'
import App from './App.vue'

initSessionRecorder();
initGtm(router);
trackApiRequests();
trackWebVitals();

const app = createApp(App);

const globals = app.config.globalProperties;

globals.$geojsonurl = '//slickcactus.com/static/jsonsets'
globals.$formDrawerWidth = 600
globals.$devmode = false

if (window.location.host.includes('localhost')) {
  globals.$geojsonurl = '//' + window.location.host + '/static/jsonsets'
  globals.$devmode = true
} else if (window.location.host === '192.168.7.156:8080') {
  globals.$geojsonurl = '//' + window.location.host + '/static/jsonsets'
} else if (
  window.location.host === 'dev.app.slickcactus.com' ||
  window.location.host === 'dev.slickcactus.com' ||
  window.location.host.includes('192.168') ||
  window.location.host === 'pagination.slickcactus.com'
) {
  // @ts-ignore
  axios.defaults.baseURL = process.env.VUE_APP_V1_API_URL;
  globals.$geojsonurl = '//' + window.location.host + '/static/jsonsets'
  globals.$devmode = true
}

// filters from utils/filters — template pipes are rewritten to $filters.x() under Vue 3
globals.$filters = filters;

globals.$placeholderCompanyImg = placeholderCompanyImg;
globals.$placeholderPropertyImg = placeholderPropertyImg;
globals.$placeholderListingImg = placeholderListingImg;
globals.$currencyPrefix = '$';
globals.$buildingSizeSuffix = 'SF';
globals.$landSizeSuffix = 'ACRES';
globals.$landSize2Suffix = 'SF';

app.config.performance = true;

globals.$eventBus = eventBus;

router.beforeEach((to, from, next) => {
  const page = document.documentElement
  if (page.classList.contains('ov-hidden')) page.classList.remove('ov-hidden')
  if (to.query.logout) {
    store.dispatch(RootActions.LOG_OUT);
    localStorage.clear()
    return true
  } else {
    next()
    return true
  }
})

registerNormalizeHtmlDirective(app);

app.use(InputFacade as unknown as Plugin);
app.use(VueDOMPurifyHTML);

registerWorker().catch((e) => {
  console.error(e.message);
});

// Vite's event for a lazily imported module (or its CSS) that failed to load; route chunks are
// also caught by router.onError, the cooldown keeps it to one reload
window.addEventListener('vite:preloadError', () => {
  recoverFromStaleBuild();
});

Sentry.init({
  app,
  dsn: process.env.VUE_APP_SENTRY_URL,
  integrations: [
    new Sentry.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ['localhost', 'dev.app.slickcactus.com', 'app.slickcactus.com', '192.168.1.6'],
    }),
  ],
  initialScope: {
    // @ts-ignore
    user: store.state.user,
    // BETA-CLEANUP: keeps the beta's errors apart from the current app's
    tags: { app_channel: isBetaApp ? 'beta' : 'current' },
  },
  tracesSampleRate: 1.0,
});

if (!store.state.visitorId) {
  getFingerprint()
    .then((fp) => {
      store.commit(RootMutations.SET_VISITOR_ID, fp);
    })
    .catch((error) => console.error(error));
}

app.use(router);
app.use(store);
app.use(vuetify);
app.use(i18n);

// Vue Router 4 resolves the initial navigation asynchronously, so mounting first
// would let that navigation's guard fire CANCEL_PENDING_REQUESTS and abort the
// boot-time requests App.vue starts in beforeMount (notably auth/me).
router.isReady().finally(() => {
  app.mount('#app');
  // index.html paints the page dark before boot on a dark load; from here the
  // Vuetify theme owns the ground, so a runtime switch to Light is not left
  // with a dark page edge.
  document.documentElement.style.removeProperty('background-color');
  document.documentElement.style.removeProperty('color-scheme');
});
